// EP-133 §3c (T133-021) — the dashboard's "today" strip (staff-on-shift /
// visitor-today / not-yet-arrived counts) and the staff front-desk arrivals
// view's backing list. Both endpoints already exist on the BE (daily-strip
// counts from indexed AR range queries; arrivals = GET /api/visitor_visits
// with a starts_from/starts_to date range).
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'

export interface TodayStrip {
  staff_on_shift: number
  visitors_today: number
  not_yet_arrived: number
}

export interface VisitorVisitSummary {
  id: number
  status: 'booked' | 'arrived' | 'departed' | 'cancelled' | 'no_show'
  starts_at: string
  ends_at: string
  purpose: string | null
  property_id: number
  property_name: string
  unit_id: number | null
  unit_name: string | null
  visitor: {
    id: number
    first_name: string
    last_name: string
    phone: string
  }
  created_via: 'widget' | 'staff' | 'resident_liff'
}

interface Paginated<T> {
  data: T[]
  meta: {
    page: number
    pages: number
    count: number
    per_page: number
  }
}

// `enabled` carries the visitor-management dual gate from the caller. It
// defaults to true so existing callers are unaffected; TodayStrip passes the
// resolved gate so a disabled account never fires the request that the server
// answers with 403 "Module not enabled".
export function useTodayStrip(enabled: boolean = true) {
  return useQuery({
    enabled,
    queryKey: ['dashboard', 'today-strip'],
    queryFn: async () => {
      const { data } = await api.get<{ data: TodayStrip }>('/api/dashboards/today_strip')
      return data.data
    },
    // 1-minute global staleTime (lib/queryClient.ts) is the floor; this
    // strip drives the admin's "what's happening today" glance so a 30s
    // fresh window is worth the bandwidth.
    staleTime: 30 * 1000,
  })
}

// Today's arrivals for the staff front-desk view. Returns all visits whose
// starts_at falls in [start_of_day, end_of_day) — INCLUDES departed/cancelled
// so the operator can see the whole day's history (not just the open list).
// Sort order is starts_at ASC so the upcoming visits surface first.
// MON-18 C: optional propertyId scopes to the header's selected building
// (the controller already accepts property_id); omitted entirely shows
// arrivals across every building, same as before.
export function useTodayArrivals(propertyId?: number) {
  return useQuery({
    queryKey: ['visitor_visits', 'today', propertyId ?? null],
    queryFn: async () => {
      const start = new Date()
      start.setHours(0, 0, 0, 0)
      const end = new Date(start)
      end.setDate(end.getDate() + 1)
      const params: Record<string, unknown> = {
        starts_from: start.toISOString(),
        starts_to: end.toISOString(),
        per_page: 100,
      }
      if (propertyId) params.property_id = propertyId
      const { data } = await api.get<Paginated<VisitorVisitSummary>>('/api/visitor_visits', { params })
      return data.data
    },
    staleTime: 30 * 1000,
  })
}

export function useCheckInVisit() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await api.post<{ data: VisitorVisitSummary }>(
        `/api/visitor_visits/${id}/check_in`
      )
      return data.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['visitor_visits'] })
      qc.invalidateQueries({ queryKey: ['dashboard', 'today-strip'] })
    },
  })
}

export function useCheckOutVisit() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await api.post<{ data: VisitorVisitSummary }>(
        `/api/visitor_visits/${id}/check_out`
      )
      return data.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['visitor_visits'] })
      qc.invalidateQueries({ queryKey: ['dashboard', 'today-strip'] })
    },
  })
}

export function useCancelVisit() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await api.post<{ data: VisitorVisitSummary }>(
        `/api/visitor_visits/${id}/cancel`
      )
      return data.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['visitor_visits'] })
      qc.invalidateQueries({ queryKey: ['dashboard', 'today-strip'] })
    },
  })
}